import { useEffect, useRef, useMemo, useState } from 'react';
import useMeQuery from '@hooks/useMeQuery';
import { useVAHSubscription } from '@hooks/useVAHSubscription';
import { useLazyQuery } from '@apollo/client';
import { millisecondsUntilDate } from '@tools/timeUtilities';
import { StreamChat } from 'stream-chat';
import { StreamType } from '@components/chat/shared/useStreamChat';
import { FormattedError } from '@context/StreamClientContext';
import { CHECK_ATTENDEE_HAS_CHANNELS } from '@graphql/operations/attendeeMessaging/checkAttendeeHasChannels';
import { ON_ATTENDEE_HAS_CHANNELS } from '@graphql/operations/attendeeMessaging/onAttendeeHasChannels';

type UseMessagingSubscriptionProps = {
  verifyBuffer?: number;
  getClient?: (isRetry?: boolean) => Promise<{
    client?: StreamChat<StreamType>;
    error?: FormattedError;
  }>;
  client?: StreamChat<StreamType>;
};
const useMessagingSubscription = ({ verifyBuffer = 60, getClient, client }: UseMessagingSubscriptionProps = {}) => {
  const checkAttendeeHasChannelsTimerRef = useRef(null);
  const [hasNewChannels, setHasNewChannels] = useState(false);

  /** Queries and Mutations */
  const {
    data: { me: { id: attendeeId, attendeeHasChannels, event: { id: eventId } = {} } = {} } = {},
    loading,
    refetch
  } = useMeQuery();

  const [checkAttendeeHasChannels, { data }] = useLazyQuery(CHECK_ATTENDEE_HAS_CHANNELS, {
    fetchPolicy: 'no-cache'
  });
  /** End of Queries and Mutations */

  useEffect(() => {
    if (
      attendeeHasChannels?.status !== 'busy' ||
      (data !== undefined && data.checkAttendeeHasChannels.status !== 'busy')
    ) {
      return;
    }

    const recheckAttendeeHasChannels = async () => {
      const nextAttempt =
        millisecondsUntilDate(data?.checkAttendeeHasChannels.resetAt || attendeeHasChannels?.resetAt) || 0;

      // Add a random offset to prevent overloading our backend
      // by spreading the load out evenly over resetAt to resetAt + 1 minute
      const randomOffset = Math.floor(Math.random() * Math.floor(verifyBuffer * 1000));

      checkAttendeeHasChannelsTimerRef.current = setTimeout(() => {
        checkAttendeeHasChannelsTimerRef.current = null;
        checkAttendeeHasChannels();
      }, nextAttempt + randomOffset);
    };
    recheckAttendeeHasChannels();

    return () => {
      if (checkAttendeeHasChannelsTimerRef.current) {
        clearTimeout(checkAttendeeHasChannelsTimerRef.current);
      }
    };
  }, [attendeeHasChannels, data, checkAttendeeHasChannels, verifyBuffer]);

  const hasChannels = attendeeHasChannels?.hasChannels || data?.checkAttendeeHasChannels.hasChannels;

  useEffect(() => {
    if (!client && (hasChannels || hasNewChannels)) {
      getClient();
    }
  }, [client, hasChannels, hasNewChannels, getClient]);

  // skip the subscription, if
  // a) the attendee has existing channels,
  // b) connection to Stream has already been established,
  // c) still waiting for eventId or attendeeId to be returned
  const skipSubscription = Boolean(client || hasChannels || hasNewChannels || !eventId || !attendeeId);

  useVAHSubscription(
    useMemo(
      () => ({
        query: ON_ATTENDEE_HAS_CHANNELS,
        variables: { eventId, attendeeId },
        fetchPolicy: 'no-cache'
      }),
      [eventId, attendeeId]
    ),
    skipSubscription,
    true,
    () => {
      setHasNewChannels(true);
    },
    () => null,
    refetch
  );

  return {
    hasChannels: hasChannels || hasNewChannels,
    loading
  };
};

export default useMessagingSubscription;
